// Pure geometry for the draggable copilot Fab + its chat panel. Kept out of the component so the
// clamp / anchor math is unit-testable without a DOM.

export interface Pos {
  top: number;
  left: number;
}

export interface PanelStyle {
  top: number;
  left: number;
  transformOrigin: string;
}

export const FAB_SIZE = 40;
export const MARGIN = 24;
export const PANEL_W = 400;
export const PANEL_H = 580;
export const DRAG_THRESHOLD = 4;
export const FAB_POS_KEY = 'aiWidget.fabPos';

/** Default resting place: bottom-right. Draggable from there. */
export function defaultFabPos(viewportW: number, viewportH: number): Pos {
  return {
    top: viewportH - FAB_SIZE - MARGIN,
    left: viewportW - FAB_SIZE - MARGIN,
  };
}

/** Keep the Fab fully on-screen with a margin. Upper bound wins if the viewport is tiny. */
export function clampFabPos(pos: Pos, viewportW: number, viewportH: number): Pos {
  const maxLeft = Math.max(MARGIN, viewportW - FAB_SIZE - MARGIN);
  const maxTop = Math.max(MARGIN, viewportH - FAB_SIZE - MARGIN);
  return {
    left: Math.min(Math.max(pos.left, MARGIN), maxLeft),
    top: Math.min(Math.max(pos.top, MARGIN), maxTop),
  };
}

/**
 * Anchor the panel to the Fab: right edge aligned with the Fab, opening downward from the Fab's top.
 * Flip upward when it would overflow the bottom. Horizontal position is clamped into the viewport.
 */
export function computePanelStyle(fab: Pos, viewportW: number, viewportH: number): PanelStyle {
  // Right-align the panel with the Fab, then clamp horizontally.
  const rawLeft = fab.left + FAB_SIZE - PANEL_W;
  const maxLeft = Math.max(MARGIN, viewportW - PANEL_W - MARGIN);
  const left = Math.min(Math.max(rawLeft, MARGIN), maxLeft);
  const side = rawLeft <= MARGIN ? 'left' : 'right';

  // Open downward from the Fab's top; flip up if it would run past the bottom margin.
  const opensDown = fab.top + PANEL_H <= viewportH - MARGIN;
  const rawTop = opensDown ? fab.top : fab.top + FAB_SIZE - PANEL_H;
  const maxTop = Math.max(MARGIN, viewportH - PANEL_H - MARGIN);
  const top = Math.min(Math.max(rawTop, MARGIN), maxTop);

  return {
    top,
    left,
    transformOrigin: `${opensDown ? 'top' : 'bottom'} ${side}`,
  };
}
